import React, { useState } from 'react';
import { Sparkles, Battery, Moon, Smile, Activity, Check } from 'lucide-react';
import { useProfile } from '../context/ProfileContext';
import { localISO } from '../data/programs';

/**
 * WellbeingCheckIn — daily holistic-metrics card on the Dashboard.
 *
 * Tracks the "non-scale victories" the dev package explicitly calls out as
 * a Differentiator under Anti-Shame UX: energy, sleep quality, mood, function.
 * One tap per row (1-5 scale). Auto-saves to profile.wellbeingLog.
 *
 * If today's entry already exists, shows a confirmation strip with edit option.
 * No streak shaming, no "you missed yesterday" copy. Frame is invitational.
 */
const DIMENSIONS = [
  { key: 'energy', label: 'Energy', icon: Battery, hint: "How's your tank today?" },
  { key: 'sleep', label: 'Sleep', icon: Moon, hint: 'How well did you rest?' },
  { key: 'mood', label: 'Mood', icon: Smile, hint: "How's your headspace?" },
  { key: 'function', label: 'Function', icon: Activity, hint: 'How does your body move today?' },
];

const SCALE = [
  { v: 1, label: 'Low' },
  { v: 2, label: '' },
  { v: 3, label: 'OK' },
  { v: 4, label: '' },
  { v: 5, label: 'Strong' },
];

const WellbeingCheckIn = () => {
  const { profile, logWellbeing } = useProfile();
  const today = localISO(new Date());
  const todayEntry = (profile.wellbeingLog || {})[today];

  const [draft, setDraft] = useState(() => ({
    energy: todayEntry?.energy || 0,
    sleep: todayEntry?.sleep || 0,
    mood: todayEntry?.mood || 0,
    function: todayEntry?.function || 0,
  }));
  const [editing, setEditing] = useState(!todayEntry);

  const complete = Object.values(draft).every((v) => v > 0);
  const submit = () => {
    if (!complete) return;
    logWellbeing(draft);
    setEditing(false);
  };

  // Already-logged confirmation strip
  if (todayEntry && !editing) {
    const avg = ((todayEntry.energy + todayEntry.sleep + todayEntry.mood + todayEntry.function) / 4).toFixed(1);
    return (
      <div
        className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-5"
        data-testid="wellbeing-checkin-done"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-adaptive-sage" />
            <div className="text-xs uppercase tracking-widest text-enf-text-secondary font-semibold">Today's check-in logged</div>
          </div>
          <button
            onClick={() => setEditing(true)}
            className="text-xs text-enf-text-muted hover:text-enf-text-primary transition-colors"
            data-testid="wellbeing-edit"
          >
            Update
          </button>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {DIMENSIONS.map((d) => {
            const Icon = d.icon;
            const val = todayEntry[d.key];
            return (
              <div key={d.key} className="text-center">
                <Icon className="w-4 h-4 mx-auto mb-1 text-enf-text-muted" />
                <div className="text-[10px] uppercase tracking-wider text-enf-text-muted">{d.label}</div>
                <div className="font-display text-2xl tracking-wide leading-none mt-0.5">{val}<span className="text-enf-text-muted text-sm">/5</span></div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 text-xs text-enf-text-muted text-center">
          Today reads <span className="text-enf-text-secondary font-semibold">{avg}/5</span> overall — noted.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-5" data-testid="wellbeing-checkin">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-enf-role-info" />
        <div className="text-xs uppercase tracking-widest text-enf-text-secondary font-semibold">Today's check-in</div>
      </div>
      <p className="text-xs text-enf-text-muted mb-4">A quick read on how you're showing up. No scoring, no shaming — just signal.</p>

      <div className="space-y-3">
        {DIMENSIONS.map((d) => {
          const Icon = d.icon;
          const selected = draft[d.key];
          return (
            <div key={d.key} className="flex items-center gap-3" data-testid={`wellbeing-row-${d.key}`}>
              <div className="w-20 flex items-center gap-2 flex-shrink-0">
                <Icon className="w-3.5 h-3.5 text-enf-text-muted" />
                <span className="text-sm text-enf-text-secondary">{d.label}</span>
              </div>
              <div className="flex-1 grid grid-cols-5 gap-1.5">
                {SCALE.map(({ v }) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setDraft({ ...draft, [d.key]: v })}
                    className={`py-1.5 rounded-md text-xs font-medium border transition-colors ${
                      selected === v
                        ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary'
                        : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:border-enf-surface-border hover:text-enf-text-primary'
                    }`}
                    data-testid={`wellbeing-${d.key}-${v}`}
                    aria-label={`${d.label} ${v} of 5`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={submit}
        disabled={!complete}
        className="mt-4 w-full py-2.5 rounded-md text-sm font-semibold transition-colors bg-enf-accent hover:bg-enf-accent-hover text-enf-text-primary disabled:bg-enf-surface-mid disabled:text-enf-text-muted disabled:cursor-not-allowed"
        data-testid="wellbeing-submit"
      >
        {complete ? "Save today's check-in" : 'Pick a value for each row'}
      </button>
    </div>
  );
};

export default WellbeingCheckIn;
